import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import LabeledInput from '@/pages/newSupported/components/LabeledInput';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { postAuth, putAuth } from '@/api/ApiCalls';
import { SupplierFullDtoIn } from '@/models/in/SupplierFullDtoIn';
import { UpdateSupplierDtoOut } from '@/models/out/UpdateSupplierDtoOut';

const QUERY_URL = 'management/suppliers';

interface EditSupplierBasicDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  supplierToEdit?: SupplierFullDtoIn | null;
  onSuccess: () => void;
}

/**
 * Dialog for the basic identity + contact details of a supplier (name, tax id, phone, email).
 * Used for both create-new-supplier and edit-existing-supplier. The tax-withholding fields
 * (percentage + approval document) live in a dedicated dialog (EditSupplierTaxWithholdingDialog)
 * because they require richer flows. Bank accounts are managed elsewhere - per (fund, supplier)
 * on PaymentInfo rows created from the new-request form.
 *
 * When editing, this dialog re-sends the supplier's existing tax-withholding fields
 * unchanged so the unified PUT /suppliers/{id} endpoint doesn't clear them.
 */
const EditSupplierBasicDetailsDialog = ({
  open,
  onClose,
  supplierToEdit,
  onSuccess,
}: EditSupplierBasicDetailsDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const isEditing = !!supplierToEdit;

  const [name, setName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(supplierToEdit?.name ?? '');
    setTaxId(supplierToEdit?.taxId ?? '');
    setPhone(supplierToEdit?.phone ?? '');
    setEmail(supplierToEdit?.email ?? '');
    setError(null);
  }, [open, supplierToEdit]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError(t('suppliersManagement.dialog.nameRequired'));
      return;
    }
    if (!taxId.trim()) {
      setError(t('suppliersManagement.dialog.taxIdRequired'));
      return;
    }

    setError(null);
    setIsSubmitting(true);

    const body: UpdateSupplierDtoOut = {
      name: name.trim(),
      taxId: taxId.trim(),
      phone: phone.trim() || null,
      email: email.trim() || null,
      // For an edit, preserve the supplier's existing tax-withholding values (they're
      // managed in the dedicated EditSupplierTaxWithholdingDialog and must not be cleared
      // by this PUT). For a brand-new supplier, default to 0% so the supplier immediately
      // enters the PendingValidUntil pipeline (no "no withholding" default anymore - 0%
      // forces the secretariat to actively fill in a valid-until + approval document).
      // The backend applies the same default if percentage is null, but we send it
      // explicitly here so the create flow is unambiguous from the client side too.
      taxWithholdingPercentage: isEditing ? (supplierToEdit?.taxWithholdingPercentage ?? null) : 0,
      taxApprovalValidUntil: supplierToEdit?.taxApprovalValidUntil ?? null,
    };

    try {
      if (isEditing) {
        await putAuth<SupplierFullDtoIn, object, UpdateSupplierDtoOut>(
          `${QUERY_URL}/${supplierToEdit!.id}`, headers, {}, body,
        );
      } else {
        await postAuth<SupplierFullDtoIn, UpdateSupplierDtoOut>(QUERY_URL, headers, body);
      }
      onSuccess();
      handleClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.toLowerCase().includes('already exists')) {
        setError(t('suppliersManagement.dialog.duplicateTaxId'));
      } else {
        setError(t(isEditing
          ? 'suppliersManagement.dialog.errorUpdating'
          : 'suppliersManagement.dialog.errorCreating'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => { if (!isOpen) handleClose(); }}
      title={isEditing
        ? t('suppliersManagement.dialog.editTitle')
        : t('suppliersManagement.dialog.createTitle')}
    >
      <div className="flex flex-col gap-6 p-6 min-w-[560px]">
        <div className="flex gap-6">
          <LabeledInput
            label="suppliersManagement.name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            width="100%"
          />
          <LabeledInput
            label="suppliersManagement.taxId"
            value={taxId}
            onChange={(e) => setTaxId(e.target.value)}
            width="100%"
          />
        </div>

        <div className="flex gap-6">
          <LabeledInput
            label="suppliersManagement.phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required={false}
            width="100%"
          />
          <LabeledInput
            label="suppliersManagement.email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required={false}
            width="100%"
          />
        </div>

        {error && <p className="text-red-500 body-sm-regular">{error}</p>}

        <div className="flex gap-3 justify-end pt-2">
          <Button variant="white" onClick={handleClose} disabled={isSubmitting}>
            {t('close')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? t('saving') : t('save')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default EditSupplierBasicDetailsDialog;
